// Self-service license-key management card for the Nutrition AI Platform
// details page. Renders a list of API keys with copy/show/rename and exposes
// rotation, creation, and deletion modals.
//
// See server-side: api/accounts/service/platform_license_keys.go.
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  ClipboardDocumentCheckIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/solid";
import axios, { AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import Modal from "components/atoms/Modal";

import { error, success } from "utils";
import { LicenseKeyType } from "utils/types";

const MAX_ACTIVE_KEYS = 5;

const GRACE_OPTIONS: { value: number; label: string; helper?: string }[] = [
  { value: 0, label: "Expire now", helper: "Up to ~5 min cache delay" },
  { value: 1, label: "1 day" },
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
];

interface PlatformLicenseKeysProps {
  licenseKeys: LicenseKeyType[];
  onChange: () => void | Promise<void>;
}

// formatRelative renders an ISO timestamp as a short relative label (e.g.
// "3d ago" / "in 7d") suitable for the inline list. Falls back to the raw
// date for distant past/future to keep things unambiguous.
function formatRelative(iso?: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = date.getTime() - Date.now();
  const sign = diffMs >= 0 ? "in " : "";
  const abs = Math.abs(diffMs);
  const minutes = Math.floor(abs / (60 * 1000));
  const hours = Math.floor(abs / (60 * 60 * 1000));
  const days = Math.floor(abs / (24 * 60 * 60 * 1000));
  if (days >= 30) {
    return date.toLocaleDateString();
  }
  if (days >= 1) {
    return `${sign}${days}d${diffMs < 0 ? " ago" : ""}`;
  }
  if (hours >= 1) {
    return `${sign}${hours}h${diffMs < 0 ? " ago" : ""}`;
  }
  if (minutes >= 1) {
    return `${sign}${minutes}m${diffMs < 0 ? " ago" : ""}`;
  }
  return diffMs >= 0 ? "in <1m" : "just now";
}

function statusBadge(status: LicenseKeyType["status"], expiration: string) {
  const expIso = expiration;
  const label = (() => {
    if (status === "expired") return "Expired";
    if (status === "expiring") return `Expires ${formatRelative(expIso)}`;
    return "Active";
  })();
  const colorClasses = (() => {
    switch (status) {
      case "expired":
        return "bg-red-100 text-red-800";
      case "expiring":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-emerald-100 text-emerald-800";
    }
  })();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorClasses}`}
    >
      {label}
    </span>
  );
}

interface LicenseKeyRowProps {
  k: LicenseKeyType;
  isOnlyActive: boolean;
  onRename: (k: LicenseKeyType) => void;
  onRotate: (k: LicenseKeyType) => void;
  onExpire: (k: LicenseKeyType) => void;
  onDelete: (k: LicenseKeyType) => void;
  freshlyMintedKeyId?: string | null;
}

const LicenseKeyRow: React.FC<LicenseKeyRowProps> = ({
  k,
  isOnlyActive,
  onRename,
  onRotate,
  onExpire,
  onDelete,
  freshlyMintedKeyId,
}) => {
  const [hideKey, setHideKey] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const isFresh = freshlyMintedKeyId === k.licenseId;
  // Once a key is expired it can't be revived (no UNEXPIRE flow exists in
  // projects_api): rotation, rename, and "set expiry" are all meaningless.
  // The token-cache will refuse to mint tokens for this row regardless of
  // what we sent it. Only Delete is useful — let the user clean up the row.
  const isExpired = k.status === "expired";
  const expiredTitle = "This key is expired — delete it or create a new one";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(k.key);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch {
      error("Failed to copy key");
    }
  };

  // Render dates in plain locale form so the customer can copy-paste them
  // into a ticket. Relative-time is only shown for status freshness.
  const createdLabel = (() => {
    const d = new Date(k.created);
    return Number.isNaN(d.getTime()) || d.getFullYear() < 2000
      ? "—"
      : d.toLocaleDateString();
  })();
  const expirationLabel = (() => {
    const d = new Date(k.expiration);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
  })();

  return (
    <li
      className={`flex flex-col gap-2 py-3 px-3 ${
        isFresh ? "bg-emerald-50" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <KeyIcon
            className="h-5 w-5 flex-shrink-0 text-gray-400"
            aria-hidden="true"
          />
          <span className="font-medium text-gray-900 truncate text-sm">
            {k.name || "Default"}
          </span>
          {statusBadge(k.status, k.expiration)}
          {isFresh && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
              <CheckCircleIcon className="h-3 w-3" /> New — copy now
            </span>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {isCopied ? (
            <ClipboardDocumentCheckIcon className="h-5 w-5 flex-shrink-0 text-green-700" />
          ) : (
            <button
              type="button"
              aria-label="Copy key"
              onClick={handleCopy}
              className="text-passio-indigo"
            >
              <DocumentDuplicateIcon className="h-5 w-5 flex-shrink-0" />
            </button>
          )}
          <button
            type="button"
            aria-label={hideKey ? "Show key" : "Hide key"}
            onClick={() => setHideKey((v) => !v)}
            className="text-passio-indigo"
          >
            {hideKey ? (
              <EyeSlashIcon className="h-5 w-5 flex-shrink-0" />
            ) : (
              <EyeIcon className="h-5 w-5 flex-shrink-0" />
            )}
          </button>
          <button
            type="button"
            aria-label="Rename key"
            onClick={() => onRename(k)}
            disabled={isExpired}
            title={isExpired ? expiredTitle : "Rename this key"}
            className={
              isExpired
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-500 hover:text-gray-700"
            }
          >
            <PencilSquareIcon className="h-5 w-5 flex-shrink-0" />
          </button>
          <button
            type="button"
            aria-label="Delete key"
            onClick={() => onDelete(k)}
            disabled={isOnlyActive && !isExpired}
            title={
              isOnlyActive && !isExpired
                ? "Cannot delete the only active key — rotate it instead"
                : "Delete this key"
            }
            className={
              isOnlyActive && !isExpired
                ? "text-gray-300 cursor-not-allowed"
                : "text-red-600 hover:text-red-700"
            }
          >
            <TrashIcon className="h-5 w-5 flex-shrink-0" />
          </button>
        </div>
      </div>

      <div className="font-mono text-sm text-gray-800 break-all bg-gray-50 border border-gray-200 rounded px-2 py-1.5 select-all">
        {hideKey
          ? Array.from({ length: Math.min(k.key.length, 40) }).map((_, i) => (
              <span key={i}>&#8226;</span>
            ))
          : k.key}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
        <span>
          <span className="text-gray-400">Created</span> {createdLabel}
        </span>
        {k.status === "expired" ? (
          <span>
            <span className="text-gray-400">Expired</span> {expirationLabel}
          </span>
        ) : (
          <span>
            <span className="text-gray-400">Expires</span> {expirationLabel}
            <span className="text-gray-400">
              {" "}
              ({formatRelative(k.expiration)})
            </span>
          </span>
        )}
        {k.lastUsedAt && (
          <span>
            <span className="text-gray-400">Last used</span>{" "}
            {formatRelative(k.lastUsedAt)}
          </span>
        )}
        {!isExpired && (
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={() => onExpire(k)}
              className="inline-flex items-center text-xs font-medium text-gray-600 hover:underline"
            >
              Set expiry…
            </button>
            <button
              type="button"
              onClick={() => onRotate(k)}
              className="inline-flex items-center text-xs font-medium text-passio-indigo hover:underline"
            >
              Rotate…
            </button>
          </div>
        )}
      </div>
    </li>
  );
};

const PlatformLicenseKeysCard: React.FC<PlatformLicenseKeysProps> = ({
  licenseKeys,
  onChange,
}) => {
  // Sort: active first (by created asc), then expiring, then expired.
  const sortedKeys = useMemo(() => {
    const rank = { active: 0, expiring: 1, expired: 2 } as const;
    return [...(licenseKeys ?? [])].sort((a, b) => {
      const r = rank[a.status] - rank[b.status];
      if (r !== 0) return r;
      return new Date(a.created).getTime() - new Date(b.created).getTime();
    });
  }, [licenseKeys]);

  const activeCount = useMemo(
    () => sortedKeys.filter((k) => k.status !== "expired").length,
    [sortedKeys]
  );
  const atCap = activeCount >= MAX_ACTIVE_KEYS;

  // Track which key was most recently minted so we can highlight it in the UI
  // and surface a "copy now — store this somewhere safe" callout for one
  // render cycle. Cleared on next refresh.
  const [freshlyMintedKeyId, setFreshlyMintedKeyId] = useState<string | null>(
    null
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<LicenseKeyType | null>(null);
  const [expireTarget, setExpireTarget] = useState<LicenseKeyType | null>(null);
  const [renameTarget, setRenameTarget] = useState<LicenseKeyType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LicenseKeyType | null>(null);

  const [createName, setCreateName] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [rotateGraceDays, setRotateGraceDays] = useState<number>(7);
  const [rotateNewName, setRotateNewName] = useState("");
  const [rotateSubmitting, setRotateSubmitting] = useState(false);

  const [expireGraceDays, setExpireGraceDays] = useState<number>(7);
  const [expireSubmitting, setExpireSubmitting] = useState(false);

  const [renameValue, setRenameValue] = useState("");
  const [renameSubmitting, setRenameSubmitting] = useState(false);

  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Reset the highlighted "freshly minted" key whenever the parent reloads
  // the list, so the callout doesn't stick around forever.
  useEffect(() => {
    if (!freshlyMintedKeyId) return;
    const exists = sortedKeys.some((k) => k.licenseId === freshlyMintedKeyId);
    if (!exists) setFreshlyMintedKeyId(null);
  }, [sortedKeys, freshlyMintedKeyId]);

  const findNewlyMintedKey = useCallback(
    (oldKeys: LicenseKeyType[], nextKeys: LicenseKeyType[]) => {
      const oldIds = new Set(oldKeys.map((k) => k.licenseId));
      return (
        nextKeys.find((k) => !oldIds.has(k.licenseId) && k.status !== "expired")
          ?.licenseId ?? null
      );
    },
    []
  );

  const refreshAndHighlight = useCallback(
    async (prev: LicenseKeyType[]) => {
      await onChange();
      try {
        const res = await axios.get<{ licenseKeys: LicenseKeyType[] }>(
          "/accounts/products/nutrition-platform/license-keys"
        );
        const next = res.data?.licenseKeys ?? [];
        const minted = findNewlyMintedKey(prev, next);
        if (minted) setFreshlyMintedKeyId(minted);
      } catch (e) {
        // Non-fatal — onChange already triggered the canonical refresh.
      }
    },
    [findNewlyMintedKey, onChange]
  );

  const handleCreateSubmit = async () => {
    setCreateSubmitting(true);
    const previousKeys = sortedKeys;
    try {
      await axios.post("/accounts/products/nutrition-platform/license-keys", {
        name: createName.trim(),
      });
      success("API key created");
      setCreateOpen(false);
      setCreateName("");
      await refreshAndHighlight(previousKeys);
    } catch (e) {
      const ax = e as AxiosError<{ cause?: string }>;
      error(ax.response?.data?.cause || "Failed to create API key");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleRotateSubmit = async () => {
    if (!rotateTarget) return;
    setRotateSubmitting(true);
    const previousKeys = sortedKeys;
    try {
      await axios.post(
        `/accounts/products/nutrition-platform/license-keys/${rotateTarget.licenseId}/rotate`,
        {
          graceDays: rotateGraceDays,
          newKeyName: rotateNewName.trim() || undefined,
        }
      );
      success(
        rotateGraceDays === 0
          ? "Key rotated. Old key expires now (up to 5 min cache delay)."
          : `Key rotated. Old key expires in ${rotateGraceDays} day${
              rotateGraceDays > 1 ? "s" : ""
            }.`
      );
      setRotateTarget(null);
      setRotateGraceDays(7);
      setRotateNewName("");
      await refreshAndHighlight(previousKeys);
    } catch (e) {
      const ax = e as AxiosError<{ cause?: string }>;
      error(ax.response?.data?.cause || "Failed to rotate key");
    } finally {
      setRotateSubmitting(false);
    }
  };

  const handleExpireSubmit = async () => {
    if (!expireTarget) return;
    setExpireSubmitting(true);
    try {
      await axios.patch(
        `/accounts/products/nutrition-platform/license-keys/${expireTarget.licenseId}`,
        { expireInDays: expireGraceDays }
      );
      success(
        expireGraceDays === 0
          ? "Key expired (up to 5 min cache delay)."
          : `Key set to expire in ${expireGraceDays} day${
              expireGraceDays > 1 ? "s" : ""
            }.`
      );
      setExpireTarget(null);
      setExpireGraceDays(7);
      await onChange();
    } catch (e) {
      const ax = e as AxiosError<{ cause?: string }>;
      error(ax.response?.data?.cause || "Failed to set expiry");
    } finally {
      setExpireSubmitting(false);
    }
  };

  const handleRenameSubmit = async () => {
    if (!renameTarget) return;
    setRenameSubmitting(true);
    try {
      await axios.patch(
        `/accounts/products/nutrition-platform/license-keys/${renameTarget.licenseId}`,
        {
          name: renameValue.trim(),
        }
      );
      success("Key renamed");
      setRenameTarget(null);
      setRenameValue("");
      await onChange();
    } catch (e) {
      const ax = e as AxiosError<{ cause?: string }>;
      error(ax.response?.data?.cause || "Failed to rename key");
    } finally {
      setRenameSubmitting(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await axios.delete(
        `/accounts/products/nutrition-platform/license-keys/${deleteTarget.licenseId}`
      );
      success("Key deleted");
      setDeleteTarget(null);
      await onChange();
    } catch (e) {
      const ax = e as AxiosError<{ cause?: string }>;
      error(ax.response?.data?.cause || "Failed to delete key");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <Card className="sm:p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-medium">API Keys</p>
          <p className="text-sm text-gray-500">
            Up to {MAX_ACTIVE_KEYS} active keys per account.
          </p>
        </div>
        <Button
          onClick={() => {
            setCreateName("");
            setCreateOpen(true);
          }}
          disabled={atCap}
          title={
            atCap
              ? `Maximum of ${MAX_ACTIVE_KEYS} active keys reached`
              : "Create a new API key"
          }
        >
          <PlusIcon className="h-4 w-4 mr-1" /> New API key
        </Button>
      </div>

      <div className="mt-4">
        <ul
          role="list"
          className="divide-y divide-gray-200 rounded-md border border-gray-200"
        >
          {sortedKeys.length === 0 && (
            <li className="py-6 text-center text-sm text-gray-500">
              No API keys yet.
            </li>
          )}
          {sortedKeys.map((k) => (
            <LicenseKeyRow
              key={k.licenseId}
              k={k}
              isOnlyActive={activeCount === 1 && k.status !== "expired"}
              onRename={(key) => {
                setRenameTarget(key);
                setRenameValue(key.name ?? "");
              }}
              onRotate={(key) => {
                setRotateTarget(key);
                setRotateGraceDays(7);
                setRotateNewName(key.name ?? "");
              }}
              onExpire={(key) => {
                setExpireTarget(key);
                setExpireGraceDays(7);
              }}
              onDelete={(key) => setDeleteTarget(key)}
              freshlyMintedKeyId={freshlyMintedKeyId}
            />
          ))}
        </ul>
        <p className="text-gray-500 text-xs ml-1 mt-2">
          Rotating a key affects all Passio API products that share this
          customer&apos;s authentication.
        </p>
      </div>

      {/* New API key modal */}
      <Modal
        isOpen={createOpen}
        setOpen={setCreateOpen}
        title="Create API key"
        submitButtonName="Create"
        buttonType="primary"
        onSubmitted={handleCreateSubmit}
        disabled={createSubmitting}
        isLoading={createSubmitting}
      >
        <div className="text-left">
          <label className="block text-sm font-medium text-gray-700">
            Key name (optional)
          </label>
          <input
            type="text"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            maxLength={64}
            placeholder="e.g. Production, CI, Mobile"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-passio-indigo focus:outline-none focus:ring-1 focus:ring-passio-indigo"
          />
          <p className="mt-2 text-xs text-gray-500">
            A new long-lived API key will be created. Copy it from the list
            after creation — it will not be shown again separately.
          </p>
        </div>
      </Modal>

      {/* Rotate modal */}
      <Modal
        isOpen={rotateTarget !== null}
        setOpen={(open) => !open && setRotateTarget(null)}
        title={`Rotate "${rotateTarget?.name || "Default"}"`}
        submitButtonName="Rotate key"
        buttonType={rotateGraceDays === 0 ? "warning" : "primary"}
        onSubmitted={handleRotateSubmit}
        disabled={rotateSubmitting}
        isLoading={rotateSubmitting}
      >
        <div className="text-left space-y-3">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-700" />
            <div>
              A fresh API key will be created. The old key will continue to work
              for the grace period you choose below, then stop issuing tokens.
              Updates may take up to 5 minutes to fully propagate.
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-700">
            How long should the old key keep working?
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            {GRACE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRotateGraceDays(opt.value)}
                className={`rounded-md border px-3 py-2 text-sm ${
                  rotateGraceDays === opt.value
                    ? "border-passio-indigo bg-indigo-50 text-passio-indigo"
                    : "border-gray-300 text-gray-700 hover:border-gray-400"
                }`}
              >
                <div className="font-medium">{opt.label}</div>
                {opt.helper && (
                  <div className="text-[10px] text-gray-500">{opt.helper}</div>
                )}
              </button>
            ))}
          </div>
          <label className="block text-sm font-medium text-gray-700 mt-2">
            New key name (optional)
          </label>
          <input
            type="text"
            value={rotateNewName}
            onChange={(e) => setRotateNewName(e.target.value)}
            maxLength={64}
            placeholder="Carry over old name by default"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-passio-indigo focus:outline-none focus:ring-1 focus:ring-passio-indigo"
          />
        </div>
      </Modal>

      {/* Expire modal — sets expiration on the existing key without minting
          a replacement. Used to retire a key on a schedule. */}
      <Modal
        isOpen={expireTarget !== null}
        setOpen={(open) => !open && setExpireTarget(null)}
        title={`Expire "${expireTarget?.name || "Default"}"`}
        submitButtonName={expireGraceDays === 0 ? "Expire now" : "Set expiry"}
        buttonType={expireGraceDays === 0 ? "warning" : "primary"}
        onSubmitted={handleExpireSubmit}
        disabled={expireSubmitting}
        isLoading={expireSubmitting}
      >
        <div className="text-left space-y-3">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-700" />
            <div>
              No new key will be created. This key will stop issuing tokens
              after the selected period. Updates may take up to 5 minutes to
              fully propagate.
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-700">
            When should this key stop working?
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            {GRACE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setExpireGraceDays(opt.value)}
                className={`rounded-md border px-3 py-2 text-sm ${
                  expireGraceDays === opt.value
                    ? "border-passio-indigo bg-indigo-50 text-passio-indigo"
                    : "border-gray-300 text-gray-700 hover:border-gray-400"
                }`}
              >
                <div className="font-medium">{opt.label}</div>
                {opt.helper && (
                  <div className="text-[10px] text-gray-500">{opt.helper}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </Modal>

      {/* Rename modal */}
      <Modal
        isOpen={renameTarget !== null}
        setOpen={(open) => !open && setRenameTarget(null)}
        title="Rename key"
        submitButtonName="Save"
        buttonType="primary"
        onSubmitted={handleRenameSubmit}
        disabled={renameSubmitting}
        isLoading={renameSubmitting}
      >
        <div className="text-left">
          <label className="block text-sm font-medium text-gray-700">
            Key name
          </label>
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            maxLength={64}
            placeholder="e.g. Production"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-passio-indigo focus:outline-none focus:ring-1 focus:ring-passio-indigo"
          />
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteTarget !== null}
        setOpen={(open) => !open && setDeleteTarget(null)}
        title={`Delete "${deleteTarget?.name || "Default"}"`}
        description="This will immediately revoke the key. Integrations using it will stop working. Up to ~5 minutes of cache lag may apply."
        submitButtonName="Delete key"
        buttonType="warning"
        type="error"
        onSubmitted={handleDeleteSubmit}
        disabled={deleteSubmitting}
        isLoading={deleteSubmitting}
      />
    </Card>
  );
};

export default PlatformLicenseKeysCard;
